/**
 * Les mots des dialogues d'un geste de séance, par voix : annuler une absence ou un retard
 * signalés, et « Gérer le rdv » hors cours de groupe.
 *
 * Voix parent : les chaînes du legacy, telles que l'espace parent les sert aujourd'hui
 * (`useLiveSessionsListState`). Voix élève : les mêmes, tutoyées — la carte de séance de l'élève
 * (`StudentLiveCardComponent`) le vouvoyait.
 */
import { type Voice } from "../../utils/voice"

/** « Gérer le rdv » quand la séance ne se décale pas dans l'app : l'essai, puis le cours qui se règle avec le tuteur. */
export const MANAGE_RDV_TEXTS: Record<
    Voice,
    {
        trial: { message: string; whatsApp: string; email: string }
        contactTutor: { message: string; contact: string }
    }
> = {
    parent: {
        trial: {
            message:
                "Pour toute question concernant votre séance d'essai (report, annulation), contactez notre équipe.",
            whatsApp: "WhatsApp",
            email: "Email",
        },
        contactTutor: {
            message:
                "Pour toute question de planification (absence, retard, déplacement de séance), contactez directement votre tuteur. Il pourra vous répondre et adapter le rdv si besoin.",
            contact: "Contacter le tuteur",
        },
    },
    student: {
        trial: {
            message: "Pour toute question concernant ta séance d'essai (report, annulation), contacte notre équipe.",
            whatsApp: "WhatsApp",
            email: "Email",
        },
        contactTutor: {
            message:
                "Pour toute question de planification (absence, retard, déplacement de séance), contacte directement ton tuteur. Il pourra te répondre et adapter le rdv si besoin.",
            contact: "Contacter mon tuteur",
        },
    },
}

export const TRIAL_TITLE = "Séance d'essai"
export const BACK_LABEL = "Retour"

/** Le titre du dialogue « contacte ton tuteur », d'après le type de salle (legacy `getSessionTypeLabel`). */
export function contactTutorTitle(roomType: string | undefined): string {
    if (roomType === "support") return "Live de support technique"
    if (roomType === "custom") return "Live personnalisé"
    return "Séance individuelle"
}

type CancelFlagKind = "absence" | "late"

export const CANCEL_FLAG_TITLES: Record<CancelFlagKind, string> = {
    absence: "Annuler l'absence ?",
    late: "Annuler le retard ?",
}

const CANCELLED_THING: Record<CancelFlagKind, string> = { absence: "l'absence", late: "le retard" }

/** `when` arrive écrit : « 12 octobre 2026 à 18h00 ». */
export const CANCEL_FLAG_QUESTION: Record<Voice, (kind: CancelFlagKind, when: string) => string> = {
    parent: (kind, when) => `Êtes-vous sûr de vouloir annuler ${CANCELLED_THING[kind]} du ${when} ?`,
    // « Es-tu sûr » s'accorde ; la question se pose sans accord.
    student: (kind, when) => `Veux-tu vraiment annuler ${CANCELLED_THING[kind]} du ${when} ?`,
}

export const CANCEL_CONFIRM_LABEL = "Oui, annuler"
export const CANCEL_KEEP_LABEL = "Non"

/** Ce qu'annuler l'absence emporte quand un rattrapage y est lié ; `when` absent : sa date n'est pas connue. */
export function recoveryAlsoCancelled(when?: string): string {
    return when != null
        ? `Le rattrapage prévu le ${when} sera également annulé.`
        : "Le rattrapage planifié sera également annulé."
}
